/**
 * The header — the wordmark, "Competition · <team_id>", EN/繁, the theme, and sign out.
 *
 * The worker's age and the clock were taken out in the 2026-10-22 redesign: they were the header
 * talking about the machine, and a teammate reads neither. A dead worker is still impossible to
 * miss: it takes over the page above Today (Console.tsx).
 */

import { useNavigate } from "react-router-dom";
import { signOut } from "../../api/auth";
import { useCompetition } from "../../store/competition";
import { useSession } from "../../store/session";
import { themeLabel, useT, useUi } from "../../i18n/useT";
import { Logo } from "../ui/Logo";

export function Header() {
  const { t } = useT();
  const navigate = useNavigate();
  const team = useCompetition((s) => s.today.data?.team_id);
  const status = useCompetition((s) => s.stream.status);

  async function leave() {
    await signOut().catch(() => undefined);
    useSession.getState().cleared();
    navigate("/signin", { replace: true });
  }

  return (
    <header className="top" data-stream-status={status}>
      <div className="top__brand">
        <span className="top__mark">
          <Logo mark="board" text={t("app.name")} rows={14} />
        </span>
        <span className="top__team">
          {t("hdr.competition")}
          {team ? ` · ${team}` : ""}
        </span>
      </div>

      <div className="top__right">
        {status !== "live" ? (
          <span className="top__stream" data-stream={status}>
            {t(`hdr.stream.${status}`)}
          </span>
        ) : null}
        <Toggles />
        <button type="button" className="btn" onClick={() => void leave()}>
          {t("hdr.signOut")}
        </button>
      </div>
    </header>
  );
}

/**
 * Language, then the theme icon — the old strip's pair, moved here (SPEC_5 §3.1). The sign-in page
 * wears it too.
 *
 * The icons are inline SVG: the theme button shows a pair whose visibility CSS switches with
 * `data-theme`, so the pick is never a frame behind the stylesheet. **The button shows the act, not
 * the state**: a sun on the dark board, a moon on paper, with a bilingual `aria-label` naming it.
 */
export function Toggles() {
  const { t, lang } = useT();
  const setLang = useUi((s) => s.setLang);
  const toggleTheme = useUi((s) => s.toggleTheme);
  // Read so the label re-renders when the theme changes; `themeLabel` reads what is on screen.
  useUi((s) => s.theme);
  const label = themeLabel(lang);

  return (
    <div className="toggles">
      <div className="seg" role="group" aria-label={t("ui.language")}>
        <button type="button" aria-pressed={lang === "en"} onClick={() => setLang("en")}>
          <span>{t("lang.en")}</span>
        </button>
        <button
          type="button"
          lang="zh-Hant"
          aria-pressed={lang === "zh-Hant"}
          onClick={() => setLang("zh-Hant")}
        >
          <span>{t("lang.zh")}</span>
        </button>
      </div>

      <button
        type="button"
        className="iconbtn"
        data-theme-toggle=""
        aria-label={label}
        title={label}
        onClick={toggleTheme}
      >
        <svg className="i-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <circle cx="12" cy="12" r="4.2" />
          <path d="M12 2.6v2.4M12 19v2.4M2.6 12h2.4M19 12h2.4M5.3 5.3l1.7 1.7M17 17l1.7 1.7M18.7 5.3L17 7M7 17l-1.7 1.7" />
        </svg>
        <svg className="i-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="M20.5 14.3A8.6 8.6 0 0 1 9.7 3.5a8.6 8.6 0 1 0 10.8 10.8z" />
        </svg>
      </button>
    </div>
  );
}
